"use client"

import { useDictionary } from "@/core/hooks/use-dictionary";
import { Control, Controller, FieldErrors, useFieldArray, UseFormRegister } from "react-hook-form";
import { MultilingualInput, MultilingualTextareaInput } from "../GlobalWidgetEditor";
import TextInput from "../../input/TextInput";
import LinkSelector from "../../LinkSelector";
import { Label } from "@/components/ui/label";
import { Heading } from "@/components/ui/Heading";
import { Button } from "@/components/ui/button";
import { Plus, Trash2 } from "lucide-react";
import FileUploadInput from "../../input/MultipleFileUploadInput";

interface NewsGridWidgetProps {
    widgetIndex: number;
    name: string;
    control: Control<any>;
    register: UseFormRegister<any>;
    errors: FieldErrors<any>;
    disabled?: boolean;
}

export const NewsGridWidget = ({
    widgetIndex,
    name,
    control,
    register,
    errors,
    disabled,
}: NewsGridWidgetProps) => {
    const { dictionary } = useDictionary();

    const { fields, append, remove } = useFieldArray({
        control,
        name: `${name}.${widgetIndex}.items`,
    });

    const getError = (path: string) => {
        const errorPath = path.split('.');
        let current = errors;
        for (const key of errorPath) {
            if (current && typeof key === 'string' && key in current) {
                current = (current as any)[key];
            } else {
                return undefined;
            }
        }
        return (current as any)?.message;
    };

    const addItem = () => {
        append({
            image: undefined,
            tag: '', tag_en: '', tag_ar: '',
            title: '', title_en: '', title_ar: '',
            date: '',
            readTime: '',
            link: '',
        });
    };

    const removeItem = (itemIndex: number) => {
        if (fields.length <= 1) return;
        remove(itemIndex);
    };

    return (
        <>
            <MultilingualInput
                baseName={`${name}.${widgetIndex}.title`}
                label={dictionary.forms?.title}
                register={register}
                errors={errors}
                disabled={disabled}
            />

            <MultilingualTextareaInput
                baseName={`${name}.${widgetIndex}.description`}
                label={dictionary.forms?.description}
                register={register}
                errors={errors}
                disabled={disabled}
            />

            {fields.map((field, itemIndex) => {
                const imageError = getError(`${name}.${widgetIndex}.items.${itemIndex}.image`) as string | undefined;

                return (
                    <div key={field.id} className="border col-span-2 p-4 rounded-md space-y-4 bg-background">
                        <div className="flex justify-between items-center">
                            <Heading level={4}>
                                کارت {itemIndex + 1}
                            </Heading>
                            <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => removeItem(itemIndex)}
                                disabled={fields.length <= 1 || disabled}
                            >
                                <Trash2 className="h-4 w-4" />
                            </Button>
                        </div>

                        <div className="space-y-1">
                            <label className="text-xs text-primary-600">تصویر کارت (نسبت پیشنهادی ۱۶:۱۰، افقی)</label>
                            <Controller
                                control={control}
                                name={`${name}.${widgetIndex}.items.${itemIndex}.image`}
                                render={({ field }) => (
                                    <FileUploadInput
                                        accept="image/*"
                                        disabled={disabled}
                                        onUploadSuccess={(image) => {
                                            if (!Array.isArray(image) && image) field.onChange(image);
                                        }}
                                        defaultImages={
                                            field.value
                                                ? [{ ...field.value, name: field.value.name || '', description: field.value.description || '' }]
                                                : []
                                        }
                                        onDefaultRemove={() => field.onChange(undefined)}
                                        error={imageError}
                                    />
                                )}
                            />
                        </div>

                        <MultilingualInput
                            baseName={`${name}.${widgetIndex}.items.${itemIndex}.tag`}
                            label="برچسب دسته (مثلاً اخبار)"
                            register={register}
                            errors={errors}
                            disabled={disabled}
                        />

                        <MultilingualInput
                            baseName={`${name}.${widgetIndex}.items.${itemIndex}.title`}
                            label={dictionary.forms?.title}
                            register={register}
                            errors={errors}
                            disabled={disabled}
                        />

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <TextInput
                                name={`${name}.${widgetIndex}.items.${itemIndex}.date`}
                                label="تاریخ"
                                placeholder="مثلاً ۱۴۰۴/۰۳/۲۰"
                                register={register}
                                errors={errors}
                                disabled={disabled}
                            />

                            <TextInput
                                name={`${name}.${widgetIndex}.items.${itemIndex}.readTime`}
                                label="زمان مطالعه"
                                placeholder="مثلاً ۳ دقیقه"
                                register={register}
                                errors={errors}
                                disabled={disabled}
                            />
                        </div>

                        <div className="space-y-1">
                            <Label className="text-xs font-medium text-muted-foreground">
                                {dictionary.forms?.link}
                            </Label>
                            <Controller
                                control={control}
                                name={`${name}.${widgetIndex}.items.${itemIndex}.link`}
                                render={({ field: linkField }) => (
                                    <LinkSelector
                                        value={linkField.value || ''}
                                        onChange={linkField.onChange}
                                        placeholder={dictionary.forms?.link}
                                    />
                                )}
                            />
                        </div>
                    </div>
                );
            })}

            <Button type="button" variant="outline" onClick={addItem} disabled={disabled} className="mt-2">
                <Plus className="h-4 w-4 mr-2" />
                {dictionary.common.add + " " + dictionary.forms.item}
            </Button>
        </>
    );
};
